import type { JSX, ReactNode } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Chip } from '@heroui/react';

import type { PatternRule, RulePriority } from '../patternXml';

/**
 * Renders a segment's inline markdown body. Owned by the container
 * (`PatternContent`) so these display components never import a markdown
 * renderer themselves — see frontend-component-patterns (display components
 * receive all data, including rendering, via props).
 */
export type RenderMarkdown = (text: string) => ReactNode;

const PRIORITY_CHIP_COLOR: Record<RulePriority, 'danger' | 'default' | 'primary' | 'warning'> = {
  may: 'primary',
  must: 'danger',
  should: 'warning',
  unknown: 'default',
};

interface AppliesWhenSectionProps {
  body: string;
  renderMarkdown: RenderMarkdown;
}

interface AvoidSectionProps {
  mistakes: string[];
  renderMarkdown: RenderMarkdown;
}

interface NotesSectionProps {
  body: string;
  renderMarkdown: RenderMarkdown;
}

interface RuleCardProps {
  renderMarkdown: RenderMarkdown;
  rule: PatternRule;
}

interface RulesSectionProps {
  renderMarkdown: RenderMarkdown;
  rules: PatternRule[];
}

interface VerifySectionProps {
  checks: string[];
  renderMarkdown: RenderMarkdown;
}

/** Priority badge text: the normalized priority, or the raw value for unknowns, or "Rule". */
function priorityLabel(rule: PatternRule): string {
  if (rule.priority !== 'unknown') return titleCase(rule.priority);
  if (rule.rawPriority) return titleCase(rule.rawPriority);
  return 'Rule';
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function AppliesWhenSection({ body, renderMarkdown }: AppliesWhenSectionProps): JSX.Element {
  return (
    <section className="mb-6">
      <h3 className="text-lg font-semibold mt-5 mb-2 text-foreground">When this applies</h3>
      <div className="text-foreground leading-relaxed">{renderMarkdown(body)}</div>
    </section>
  );
}

export function AvoidSection({ mistakes, renderMarkdown }: AvoidSectionProps): JSX.Element {
  return (
    <section className="mb-6">
      <h3 className="flex items-center gap-2 text-lg font-semibold mt-5 mb-2 text-foreground">
        <ExclamationTriangleIcon aria-hidden="true" className="h-5 w-5 text-warning" />
        Avoid
      </h3>
      <ul className="list-disc list-inside space-y-1 text-foreground">
        {mistakes.map((mistake, index) => (
          <li className="[&>p]:inline [&>p]:m-0" key={index}>
            {renderMarkdown(mistake)}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function NotesSection({ body, renderMarkdown }: NotesSectionProps): JSX.Element {
  return (
    <section className="mb-6">
      <h3 className="text-lg font-semibold mt-5 mb-2 text-foreground">Notes</h3>
      <div className="text-foreground leading-relaxed">{renderMarkdown(body)}</div>
    </section>
  );
}

/** A single rule: priority badge (text + color), optional id caption, and body. */
export function RuleCard({ renderMarkdown, rule }: RuleCardProps): JSX.Element {
  return (
    <li className="list-none rounded-md border border-default bg-surface-elevated p-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Chip color={PRIORITY_CHIP_COLOR[rule.priority]} size="sm" variant="flat">
          {priorityLabel(rule)}
        </Chip>
        {rule.id !== null && (
          <span className="font-mono text-xs text-muted break-all">{rule.id}</span>
        )}
      </div>
      <div className="text-sm text-foreground leading-relaxed">{renderMarkdown(rule.body)}</div>
    </li>
  );
}

export function RulesSection({ renderMarkdown, rules }: RulesSectionProps): JSX.Element {
  return (
    <section className="mb-6">
      <h3 className="text-lg font-semibold mt-5 mb-2 text-foreground">Rules</h3>
      <ul className="space-y-3">
        {rules.map((rule, index) => (
          <RuleCard key={rule.id ?? index} renderMarkdown={renderMarkdown} rule={rule} />
        ))}
      </ul>
    </section>
  );
}

export function VerifySection({ checks, renderMarkdown }: VerifySectionProps): JSX.Element {
  return (
    <section className="mb-6">
      <h3 className="flex items-center gap-2 text-lg font-semibold mt-5 mb-2 text-foreground">
        <CheckCircleIcon aria-hidden="true" className="h-5 w-5 text-success" />
        Verify
      </h3>
      <ul className="list-disc list-inside space-y-1 text-foreground">
        {checks.map((check, index) => (
          <li className="[&>p]:inline [&>p]:m-0" key={index}>
            {renderMarkdown(check)}
          </li>
        ))}
      </ul>
    </section>
  );
}
